import type { api } from "@healthos/schemas";
import { type SnapshotGoal, snapshotGoalDayMet, snapshotGoalIsForming } from "@healthos/shared";
import { CaretDown } from "@phosphor-icons/react";
import { Modal } from "../../primitives/modal";
import { BehaviourIcon } from "./behaviour-icon";
import { SNAPSHOT_BEHAVIOURS } from "./snapshot-tokens";

const behaviourOf = (id: string) => SNAPSHOT_BEHAVIOURS.find((b) => b.id === id) ?? null;

/** Past the target, "7 of the 5 days it needs" reads as a mistake. */
const goalCount = (met: number, need: number, noun: string) =>
  met >= need
    ? `${met} ${noun}, past the ${need} it needs`
    : `${met} of the ${need} ${noun} it needs`;

const dayMonth = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });

export interface SnapshotGoalActions {
  onChoose: (outcomeId: string) => void;
  onRenew: () => void;
  onEnd: () => void;
}

/**
 * The member's one goal, measured against their own days. It states what they
 * chose, how the window is going, and where their data disagrees. It does not
 * grade them: the day squares are what happened, and a goal that lapses does so
 * quietly.
 */
export function SnapshotGoalBlock({
  goal,
  onManage,
}: {
  goal: SnapshotGoal | null;
  onManage: () => void;
}) {
  const b = goal ? behaviourOf(goal.behaviour) : null;
  const forming = goal ? snapshotGoalIsForming(goal) : false;
  return (
    <section
      className="snap-goal"
      style={b ? ({ ["--accent" as string]: b.color } as React.CSSProperties) : undefined}
    >
      <div className="snap-section-head">
        <h2>Your goal</h2>
        {goal ? <span className="hint">until {dayMonth(goal.until)}</span> : null}
        <button className="snap-goal-edit" type="button" aria-haspopup="dialog" onClick={onManage}>
          Manage
          <CaretDown size={12} aria-hidden="true" />
        </button>
      </div>
      {!goal ? (
        <p className="snap-goal-none">
          No goal yet. Manage lists the outcomes you can pick from; one runs at a time.
        </p>
      ) : (
        <div className="snap-goal-row">
          <div className="snap-goal-l">
            <p className="snap-goal-label">{goal.label}</p>
            <p className="snap-goal-spec">
              {goal.supporting}
              {goal.read ? ` ${goal.read}` : ""}
            </p>
          </div>
          <div className="snap-goal-r">
            {forming || goal.series === null ? (
              <>
                <span className="snap-goal-chip is-quiet">No readings yet</span>
                <span className="snap-goal-count">
                  Nothing has reported {goal.noun} for this goal.
                </span>
              </>
            ) : (
              <>
                <span className={`snap-goal-chip ${goal.onTarget ? "is-met" : "is-over"}`}>
                  {goal.onTarget
                    ? "On target"
                    : `${goal.gap} ${goal.direction === "lte" ? "over target" : "short of target"}`}
                </span>
                <div
                  className="snap-goal-days"
                  role="img"
                  aria-label={`${goal.met} of ${goal.series.length} ${goal.noun} met the target.`}
                >
                  {goal.series
                    .map((v, i) => ({ v, back: goal.series ? goal.series.length - 1 - i : i }))
                    .map(({ v, back }) => (
                      <span
                        key={back}
                        className={`snap-goal-day${
                          v !== null && snapshotGoalDayMet(v, goal.target, goal.direction)
                            ? " is-ok"
                            : v === null
                              ? " is-none"
                              : ""
                        }`}
                      />
                    ))}
                </div>
                <div
                  className="snap-goal-prog"
                  role="img"
                  aria-label={goalCount(goal.met, goal.need, goal.noun)}
                >
                  <i style={{ width: `${Math.min(100, (goal.met / goal.need) * 100)}%` }} />
                </div>
                <span className="snap-goal-count">{goalCount(goal.met, goal.need, goal.noun)}</span>
              </>
            )}
          </div>
        </div>
      )}
      {goal?.conflict ? <p className="snap-goal-conflict">{goal.conflict}</p> : null}
    </section>
  );
}

/** Picking, renewing or ending. Choosing supersedes whatever was current. */
export function SnapshotGoalModal({
  goal,
  options,
  actions,
  onClose,
}: {
  goal: SnapshotGoal | null;
  options: ReadonlyArray<api.SnapshotGoalOption>;
  actions: SnapshotGoalActions;
  onClose: () => void;
}) {
  return (
    <Modal open onOpenChange={(next) => (next ? undefined : onClose())} title="Your goal" size="md">
      <div className="snap-gm">
        {goal ? (
          <div className="snap-gm-cur">
            <b>{goal.label}</b>
            <span>{goal.supporting}</span>
            <span className="snap-gm-when">
              Started {dayMonth(goal.startedAt)} · runs until {dayMonth(goal.until)}
            </span>
            <div className="snap-gm-acts">
              <button type="button" onClick={actions.onRenew}>
                Renew for six more weeks
              </button>
              <button
                type="button"
                className="is-end"
                onClick={() => {
                  actions.onEnd();
                  onClose();
                }}
              >
                End this goal
              </button>
            </div>
          </div>
        ) : (
          <p className="snap-gm-none">
            No goal running. Pick one below; it runs for six weeks unless you renew it.
          </p>
        )}
        <p className="snap-gm-k">
          One at a time. Picking a new goal closes the one you are on, and its record is kept.
        </p>
        {SNAPSHOT_BEHAVIOURS.map((b) => {
          const forBehaviour = options.filter((o) => o.behaviour === b.id);
          if (forBehaviour.length === 0) return null;
          return (
            <div className="snap-gm-grp" key={b.id}>
              <p
                className="snap-gm-grp-k"
                style={{ ["--accent" as string]: b.color } as React.CSSProperties}
              >
                <BehaviourIcon name={b.iconName} size={14} aria-hidden />
                {b.label}
              </p>
              {forBehaviour.map((o) => (
                <button
                  key={o.outcomeId}
                  type="button"
                  role="menuitemradio"
                  aria-checked={goal?.outcomeId === o.outcomeId}
                  className={`snap-gm-opt${goal?.outcomeId === o.outcomeId ? " is-on" : ""}`}
                  onClick={() => {
                    actions.onChoose(o.outcomeId);
                    onClose();
                  }}
                >
                  <b>{o.label}</b>
                  <span>{o.supporting}</span>
                </button>
              ))}
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
